import React, { useMemo, useState } from "react";
import {
  RideButton,
  RideGlDownloadSolid,
} from "@rimac-seguros/ride-system-components";
import { useSelector } from "react-redux";

import { usePlanService } from "@/services/plan.service";
import { usePlansContext } from "../../../context/PlansContext";
import { decryptCredentials } from "@/utils/cifrate";
import helpers from "@/utils/helpers";
import { currencyFormat } from "@/utils/currency";
import { APP_KEY_ENCRYPT } from "@/utils/awsConfig";
import { encrypt_AES256_2 } from "@/utils/CryptographyUtils";
import { getProductOptions } from "@/utils/product";

export const DownloadQuote = () => {
  const [loading, setLoading] = useState(false);

  const userData = useSelector((state) => state.vida.userData);
  const {
    typeOfFinancing,
    planSelected,
    nameFrequency,
    currencyPlan,
  } = useSelector((state) => state.plans);
  const {
    devolucionTotal,
    pagoMensual,
    valor,
    aporteTotal,
    aporte,
    gananciaTotal,
    ganancia,
  } = typeOfFinancing || {};
  const { sumAssured, savingTime, returnPercentage } = planSelected || {};

  const currentPagoMensual = pagoMensual ?? valor ?? 0;
  const currentDevolucionTotal = devolucionTotal ?? 0;
  const currentGananciaTotal = gananciaTotal ?? ganancia ?? 0;
  const currentAporteTotal = aporteTotal ?? aporte ?? 0;
  const currencySymbol = currencyPlan === "USD" ? "$" : "S/";

  const { downloadQuote } = usePlanService();
  const { isLoadingData } = usePlansContext();

  const handleDownload = async () => {
    setLoading(true);
    const { digitalDataProductName } = getProductOptions();

    helpers.getTagAction(
      digitalDataProductName || "Seguro Vida Endoso con Devolución",
      "Cotización",
      "click",
      "Descargar tu cotización",
      "trackAction",
    );

    try {
      const request = {
        ideCotizacion:
          planSelected?.idCotizacion || planSelected?.ideTransaccion,
        idPlantilla: decryptCredentials(
          process.env.REACT_APP_KEY_TEMPLATE_PDF_ENDOSABLE,
        ),
        producto: "vcd",
        dimensiones: {
          width: "1008px",
          height: "1440px",
        },
        data: {
          cliente: {
            nombre: getFullName,
            celular: userData?.telefono || userData?.celular || "-",
            correo: userData?.correo || userData?.email || "-",
            numero_documento: userData?.numeroDocumento || userData?.documento,
          },
          plan: {
            pago_mensual: `${currencySymbol}${currencyFormat(
              currentPagoMensual,
            )}`,
            devolucion_total: `${currencySymbol}${currencyFormat(
              currentDevolucionTotal,
            )}`,
            ganancia_extra: `${currencySymbol}${currencyFormat(
              currentGananciaTotal,
            )}`,
            aporte_total: `${currencySymbol}${currencyFormat(
              currentAporteTotal,
            )}`,
            cobertura_fallec: sumAssured?.text,
            tiempo_ahorro: savingTime?.text ? `${savingTime.text} años` : "",
            porcentaje_devolucion: returnPercentage?.text,
            frecuencia_pago: nameFrequency
              ? nameFrequency.charAt(0).toUpperCase() +
                nameFrequency.slice(1).toLowerCase()
              : "",
          },
        },
      };

      const { payload } = await downloadQuote(request);
      const url = payload?.fileUrl;

      if (userData?.indPrevPageApp) {
        const message = `EMBEDDED-APP-HIPERVINCULO : {"accion": "PDF" , "valor": "${url}" , "mensaje": "", "titulo": "Cotización Seguro Vida Endoso con Devolución"}`;
        const encryptedMessage = encrypt_AES256_2(message, APP_KEY_ENCRYPT);
        return helpers.sendMessageToApp(encryptedMessage);
      }
      const link = document.createElement("a");
      link.href = url;
      link.download = payload?.fileUrl?.split("Digital/")[1];
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getFullName = useMemo(() => {
    const nombres = userData?.nombres || userData?.names || "";
    const apellidoPaterno =
      userData?.apellidoPaterno || userData?.lastName || "";
    const apellidoMaterno =
      userData?.apellidoMaterno || userData?.surName || "";
    return nombres
      ? `${helpers.capitalizeFullName(nombres)} ${helpers.capitalizeFullName(
          apellidoPaterno,
        )} ${helpers.capitalizeFullName(apellidoMaterno)}`.trim()
      : "";
  }, [userData]);

  return (
    <div className="mb-20 border-t border-ride-sys-color-border-default py-6 flex justify-center">
      <RideButton
        text="Descarga tu cotización"
        variant="text-primary"
        size="medium"
        disabled={isLoadingData || loading}
        glyph={<RideGlDownloadSolid />}
        glyphPosition="right"
        onClick={handleDownload}
      />
    </div>
  );
};

export default DownloadQuote;
